<template lang="pug">
div
  AppDialog(
    title="Job"
    size="medium"
    position="centered"
    append-to-body
    has-footer
    :show="showDialog"
    @close="close"
  )
    .small.text-grey(slot="header") {{ job.summary }}
    .label.job-status.ml-10(
      slot="titleLabel"
      :class="statusClass"
    ) {{ job.status.name }}

    .d-flex.flex-column
      .d-flex.justify-content-between.mb-5.flex-row
        .d-flex.align-items-center.flex-1
          HubAvatar.avatar-overlaps(
            v-for="member in displayableMembers"
            :key="member.id"
            v-tooltip="{ delay: { hide: 0 } }"
            :title="member.text"
            :user-id="member.id"
            :users-avatars="usersAvatars"
            avatar-style="medium"
          )
          span.more-members-badge.light(v-if="showRemainingUsersBadge") {{ remainingUsersBadgeTitle }}

        .flex-1
          .d-flex.align-items-center.justify-content-end
            .hi.hi-refresh.mr-5(
              v-if="isRecurring"
              v-tooltip
              :title="recurringScheduleTooltip"
            )
            .text-right {{ job.time_range }}
              .text-grey {{ job.date }}

          .text-grey.justify-content-end.mt-5.text-right {{ job.job_site_address }}

      .mt-5(v-if="job.has_started")
        .time-table.d-flex.flex-sm-row.mt-20
          div
            .small.text-grey.text-uppercase Time worked
            p.stat-value.mt-5 {{ job.formatted_time_worked }}
          .ml-20
            .small.text-grey.text-uppercase Time started
            p.stat-value.mt-5 {{ job.formatted_time_started }}
          .ml-20
            .small.text-grey.text-uppercase Time stopped
            p.stat-value.mt-5 {{ job.formatted_time_stopped }}

    template(#footer)
      .d-flex.justify-content-between.flex-column.flex-sm-row.gap-15
        AppButton.delete(
          v-if="job.can_modify"
          type="danger"
          @click="deleteJobAndCloseDialog"
        ) Delete
        .d-flex.flex-column.flex-sm-row.gap-15
          AppButton.view(
            v-if="job.can_view"
            type="default"
            @click="viewJob"
          ) View

          AppButton.edit.ml-0.mt-0(
            v-if="job.can_modify"
            type="primary"
            @click="editJob"
          ) Edit

  ShowJobDialog#show-job-dialog(
    v-if="renderViewJobDialog"
    v-show="showViewJobDialog"
    :job="formattedJob"
    :work-order="formattedWorkOrder"
    :can-edit-jobs="canEditJobs"
    :processing-job="processingJob"
    @close="close"
    @edit-job="editJob"
    @complete-job="completeJob"
    @reopen-job="reopenJob"
  )

  JobsDialog#edit-job-dialog(
    v-if="renderEditJobDialog"
    editing
    :job="formattedJob"
    :time-zone="organization.time_zone.for"
    :time-zone-abbr="organization.time_zone.abbr"
    :real-org-time-zone="organization.time_zone.for"
    :work-order="formattedWorkOrder"
    :start-week-on="organization.start_week_on"
    :active-organization-members="organizationMembers"
    @close="close"
  )

  RecurringJobsDialogManager
</template>

<script>
import { humanizeRecurringSchedule } from '~/utils/recurring_schedule';
import ShowJobDialog from '~/components/work_orders/jobs_dialog/ShowJobDialog.vue';
import { mapInertiaProps } from '~/utils/inertia';
import JobsDialog from '~/components/work_orders/jobs_dialog/JobsDialog.vue';
import { cleanWorkOrder, cleanJob } from '~/stores/WorkOrderStore/common/utils';
import { RecurringJobs, WorkOrderJobActions } from '~/mixins';
import RecurringJobsDialogManager from '~/components/work_orders/jobs_dialog/RecurringJobsDialogManager.vue';
import WorkOrdersConfirmDeleteRecurringJobsDialog from '~/components/work_orders/WorkOrdersConfirmDeleteRecurringJobsDialog.vue';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';
import { AVATARS_MAX_DISPLAYABLE_MEMBERS } from '~/app_constants';

import { recurringJobsDialogEvents } from '~/utils/work_orders';
import { WorkOrdersJobsResource } from '~/resources';

const confirmDeleteRecurringJobsDialog = WorkOrdersConfirmDeleteRecurringJobsDialog.name;

export default {
  name: 'JobDialog',
  components: {
    ShowJobDialog,
    JobsDialog,
    RecurringJobsDialogManager,
  },
  mixins: [WorkOrderJobActions, RecurringJobs, createUsersAvatarsMixin('assignedMembers')],
  props: {
    show: {
      type: Boolean,
      default: true,
    },
    event: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    renderViewJobDialog: false,
    renderEditJobDialog: false,
    showDialog: true,
    showViewJobDialog: false,
    organizationMembers: [],
  }),
  computed: {
    ...mapInertiaProps({
      organization: 'organization',
      clients: 'clients',
      visitsReportUrl: 'visits_report_url',
      isOwnerManager: 'is_owner_manager',
    }),
    members() {
      return this.job.assignees
        .map(({ id, name: text, avatar }) => ({ id, text, avatar }))
        .sort((ua, ub) => ua.text.localeCompare(ub.text));
    },
    job() {
      return this.event.jobAttributes;
    },
    workOrder() {
      return this.job.work_order;
    },
    canEditJobs: ({ isOwnerManager: iom }) => !!iom,
    formattedWorkOrder() {
      const tz = this.organization.time_zone.for;
      const wo = this.workOrder;
      const state = { clients: this.clients, wo_assignees: this.workOrderAssignees };
      const getters = { organizationMembers: this.members };

      return cleanWorkOrder(wo, state, getters, tz);
    },
    formattedJob() {
      const getters = { organizationMembers: this.members };
      const state = {
        clients: this.clients,
        wo_assignees: this.workOrderAssignees,
        job_schedules: [this.job.schedule],
        visits_report_url: this.visitsReportUrl,
        work_order: this.workOrder,
      };
      const jobWithFormattedShifts = {
        ...this.job,
        attendance_shifts: this.job.attendance_shifts.length ? this.formatShifts(this.job.attendance_shifts) : [],
      };

      return cleanJob(jobWithFormattedShifts, this.organization.time_zone.for, state, getters);
    },
    assignedMembers() {
      return this.members.filter((user) => this.job.assignee_ids.includes(user.id));
    },
    displayableMembers() {
      return this.assignedMembers.slice(0, AVATARS_MAX_DISPLAYABLE_MEMBERS);
    },
    showRemainingUsersBadge() {
      return this.job.assignee_ids.length > AVATARS_MAX_DISPLAYABLE_MEMBERS;
    },
    remainingUsersBadgeTitle() {
      return `+${this.job.assignee_ids.length - AVATARS_MAX_DISPLAYABLE_MEMBERS}`;
    },
    statusClass() {
      return `label-${this.job.status.symbol}`;
    },
    isRecurring() {
      return this.job.schedule.repeat_type !== 'once';
    },
    recurringScheduleTooltip() {
      return humanizeRecurringSchedule(this.job.schedule);
    },
    workOrderAssignees() {
      const assignees = this.members.filter((user) => this.workOrder.assignee_ids.includes(user.id));

      return assignees.map(({ text: name, ...rest }) => ({ name, ...rest }));
    },
  },
  methods: {
    viewJob() {
      this.showDialog = false;
      this.renderViewJobDialog = true;
      this.showViewJobDialog = true;
    },
    async completeJob() {
      await this.onCompleteJob(this.job).then(this.close());
    },
    async reopenJob() {
      await this.onReopenJob(this.job).then(this.close());
    },
    async deleteJobAndCloseDialog() {
      const response = await this.confirmDeleteJob(this.job);

      if (response) {
        this.close();
        recurringJobsDialogEvents.close(confirmDeleteRecurringJobsDialog);
      }
    },
    close() {
      this.$emit('close');
    },
    async editJob() {
      await WorkOrdersJobsResource.edit({
        organizationId: this.organization.id,
        woId: this.workOrder.id,
        jobId: this.job.id,
      }).then((response) => {
        console.log(response);
        this.organizationMembers = response.map(({ id, name: text, avatar }) => ({ id, text, avatar }));
      });

      this.showDialog = false;
      this.showViewJobDialog = false;
      this.renderEditJobDialog = true;
    },
    // This is needed because the cleanJob function expects the userId in snake_case.
    // We should probably refactor the serializer or the function once the pattern between
    // snake_case vs camelCase is decided
    formatShifts(attendanceShifts) {
      return attendanceShifts.map(({ userId: user_id, ...rest }) => ({
        user_id,
        ...rest,
      }));
    },
  },
};
</script>
